import { FETCH_DAY_WEIGHTS, FETCH_MONTHLY_WEIGHTS, FETCH_MULTIPLE_DAYS_WEIGHTS } from 'actions/weightActions';
import ViewerUserPreferences from 'models/user/ViewerUserPreferences';
import moment from 'moment';
import DateFormatter from 'utils/date/DateFormatter';
import NumberParser from 'utils/parsers/NumberParser';
import Personalizer from 'personalizer';
import BiometricLimits from 'utils/settings/BiometricLimits';
import ToolTipErrorReporter from 'utils/reporters/ToolTipErrorReporter';
import Localizer from 'localizer';
import i18next from 'i18next';
import NumberFormats from 'utils/personalizer/NumberFormats';
import ConversionFactors from 'utils/personalizer/ConversionFactors';
import {
    Color100 as goalLineColor,
    Color113 as boneMassColor,
    Color227 as weightColor,
    Color228 as bodyWaterColor,
    Color710 as bmiColor,
    Color711 as bodyFatColor,
    Color712 as muscleMassColor
} from 'ui-connect/styles/default-theme';

export const CHART_52_WEEKS_LENGTH = 13;

let parser = new NumberParser({
    numberSeparator: Personalizer.getNumberSeparator()
});

export const TABS = {
    ONE_MONTH: 4,
    ONE_DAY: 3,
    SEVEN_DAYS: 2,
    FOUR_WEEKS: 1,
    FIFTY_TWO_WEEKS: 0
};

export const MULTIPLE_DAYS_REQUEST_KEYS = {
    FOUR_WEEKS: 'fourWeeksWeights',
    ONE_MONTH: 'oneMonthWeights',
    SEVEN_DAYS: 'sevenDaysWeights'
};

export const AVAILABLE_WEIGHT_METRICS = {
    weight: 'weight',
    bmi: 'bmi',
    bodyFat: 'bodyFat',
    muscleMass: 'muscleMass',
    boneMass: 'boneMass',
    bodyWater: 'bodyWater',
    physiqueRating: 'physiqueRating',
    visceralFat: 'visceralFat',
    metabolicAge: 'metabolicAge',
    caloricIntake: 'caloricIntake'
};

export const WEIGHT_METRICS_COLORS = {
    weight: weightColor,
    bmi: bmiColor,
    bodyFat: bodyFatColor,
    muscleMass: muscleMassColor,
    boneMass: boneMassColor,
    bodyWater: bodyWaterColor,
    physiqueRating: weightColor,
    visceralFat: weightColor,
    metabolicAge: weightColor,
    caloricIntake: weightColor
};

export const GOAL_LINE_CHART_COLOR = goalLineColor;

export const HEALTHY_WEIGHT_RANGES = {
    visceralFatHealthyLevel: '1 - 12',
    visceralFatExcessLevel: '13 - 59',
    bodyWaterWomenRange: '45 - 60%',
    bodyWaterMenRange: '50 - 65%'
};

export const returnErrorOrValid = (
    weightEditInput,
    viewerUserPreferences,
    displayWeightSecondInput,
    weightDisplayUnit,
    weightSecondEditInput
) => {
    const weightValue = isInputValid(weightEditInput, viewerUserPreferences);
    if (displayWeightSecondInput && weightSecondEditInput) {
        const weightSecondValue = isInputValid(weightSecondEditInput, viewerUserPreferences);
        if (
            weightValue &&
            weightSecondValue &&
            (isNaN(weightValue) ||
                isNaN(weightSecondValue) ||
                typeof weightValue === 'string' ||
                typeof weightSecondValue === 'string')
        ) {
            return weightValue;
        }

        if (
            !weightValue ||
            (weightSecondValue !== 0 && !weightSecondValue) ||
            (weightValue === BiometricLimits.minStWeight && weightSecondValue < BiometricLimits.minWeight) ||
            weightValue > BiometricLimits.maxStWeight ||
            weightSecondValue > BiometricLimits.maxLbsWeight ||
            (weightValue === BiometricLimits.maxStWeight && weightSecondValue > BiometricLimits.maxStLbsWeight)
        ) {
            return 'invalid_weight_message_short';
        }

        return true;
    }

    const minWeight =
        weightDisplayUnit === Personalizer.WeightUnitKeys.METRIC
            ? BiometricLimits.minKgWeight
            : BiometricLimits.minWeight;
    const maxWeight =
        weightDisplayUnit === Personalizer.WeightUnitKeys.METRIC
            ? BiometricLimits.maxKgWeight
            : BiometricLimits.maxWeight;

    if (weightValue && typeof weightValue === 'string') {
        return weightValue;
    }

    if (isNaN(weightValue) || weightValue > maxWeight || weightValue < minWeight) {
        return 'invalid_weight_message_short';
    }

    return true;
};

export const isInputValid = (weightEditInput, viewerUserPreferences) => {
    const weightString = weightEditInput;
    let decimalSeparator = weightString.search(/^[0-9]+(,[0-9]{3})*(\.[0-9]+)?$/);
    let commaSeparator = weightString.search(/^[0-9]+(\.[0-9]{3})*(,[0-9]+)?$/);
    let numberFormat = viewerUserPreferences.numberFormat;
    let weightValue;

    try {
        weightValue = parser.parse(weightString);
    } catch (err) {
        return 'invalid_weight_message_short';
    }

    if (numberFormat === 'decimal_comma' && commaSeparator === -1 && decimalSeparator !== -1) {
        return 'invalid_format_use_comma';
    }

    if (numberFormat === 'decimal_period' && decimalSeparator === -1 && commaSeparator !== -1) {
        return 'invalid_format_use_period';
    }

    if (isNaN(weightValue)) {
        return false;
    }
    return weightValue;
};

export const inputHasSameValueAsStart = (weightEditInput, goalValue, weightSecondEditInput, weightDisplayUnit) => {
    const inputValue = weightEditInput ? weightEditInput.getValue() : null;
    const secondInputValue = weightSecondEditInput ? weightSecondEditInput.getValue() : null;

    const weightGoalValue = Personalizer.personalizeWeight(
        goalValue,
        Personalizer.NumberFormats.PRECISION1,
        weightDisplayUnit
    );
    let weightInputValueParsed = inputValue ? inputValue : null;

    if (inputValue && secondInputValue) {
        const weightStInput = parser.parse(inputValue);
        const weightLbsInput = parser.parse(secondInputValue);
        weightInputValueParsed = (weightStInput + weightLbsInput) * Personalizer.ConversionFactors.StToLbs;
    }

    return !weightInputValueParsed || weightInputValueParsed === weightGoalValue;
};

export const validateWeightInput = (value, inputToAttachTooltipTo, component) => {
    let errorMessageOrIsValid = returnErrorOrValid(value, ViewerUserPreferences.toJSON());

    if (errorMessageOrIsValid !== true) {
        let tooltipElement = inputToAttachTooltipTo;

        if (component.tooltipError) {
            component.tooltipError.remove();
        }

        component.tooltipError = new ToolTipErrorReporter({
            message: function() {
                return Localizer.localize(errorMessageOrIsValid);
            },
            element: tooltipElement,
            container: component.toolTipContainer
        });

        component.tooltipError.report(tooltipElement, errorMessageOrIsValid);

        return false;
    } else if (component.tooltipError) {
        component.tooltipError.remove();
    }
    return true;
};

export const isBodyFatMuscleMassInputValid = (valueEditInput, viewerUserPreferences) => {
    const valueString = valueEditInput;
    let decimalSeparator = valueString.search(/^[0-9]+(,[0-9]{3})*(\.[0-9]+)?$/);
    let commaSeparator = valueString.search(/^[0-9]+(\.[0-9]{3})*(,[0-9]+)?$/);
    let numberFormat = viewerUserPreferences.numberFormat;
    let value;

    try {
        value = parser.parse(valueString);
    } catch (err) {
        return false;
    }

    if (numberFormat === 'decimal_comma' && commaSeparator === -1 && decimalSeparator !== -1) {
        return false;
    }

    if (numberFormat === 'decimal_period' && decimalSeparator === -1 && commaSeparator !== -1) {
        return false;
    }

    if (isNaN(value)) {
        return false;
    }
    return value;
};

export const validateBodyFatInput = (value) => {
    const bodyFatValue = isBodyFatMuscleMassInputValid(value, ViewerUserPreferences.toJSON());

    if (bodyFatValue && typeof bodyFatValue === 'string') {
        return bodyFatValue;
    }

    if (bodyFatValue > BiometricLimits.maxBodyFat || bodyFatValue < BiometricLimits.minBodyFat) {
        return false;
    }

    return true;
};

export const validateMuscleMassInput = (value, measurementUnit) => {
    const muscleMassValue = isBodyFatMuscleMassInputValid(value, ViewerUserPreferences.toJSON());

    if (muscleMassValue && typeof muscleMassValue === 'string') {
        return muscleMassValue;
    }

    switch (measurementUnit) {
        case Localizer.localize('weight_unit_metric'):
            if (
                muscleMassValue > BiometricLimits.maxMuscleMassKg ||
                muscleMassValue < BiometricLimits.minMuscleMassKg
            ) {
                return false;
            }
            break;
        case Localizer.localize('weight_unit_statute'):
            if (
                muscleMassValue > BiometricLimits.maxMuscleMassLbs ||
                muscleMassValue < BiometricLimits.minMuscleMassLbs
            ) {
                return false;
            }
            break;
    }

    return true;
};

export const getSevenDaysRequestDates = (date) => {
    let endDate = new moment(date, DateFormatter.LINK_FORMAT);

    return {
        endDate: endDate.format(DateFormatter.LINK_FORMAT),
        startDate: endDate.subtract(6, 'days').format(DateFormatter.LINK_FORMAT)
    };
};

export const getFourWeeksRequestDates = (date) => {
    let endDate = new moment(date, DateFormatter.LINK_FORMAT);

    return {
        endDate: endDate.format(DateFormatter.LINK_FORMAT),
        startDate: endDate.subtract(27, 'days').format(DateFormatter.LINK_FORMAT)
    };
};

export const getOneMonthRequestDates = (date) => {
    let endDate = new moment(date, DateFormatter.LINK_FORMAT).endOf('month');
    let isEndDateAfterToday = endDate.isAfter(new moment());
    let processedEndDate = !isEndDateAfterToday ? endDate : new moment();

    return {
        endDate: processedEndDate.format(DateFormatter.LINK_FORMAT),
        startDate: new moment(date, DateFormatter.LINK_FORMAT).startOf('month').format(DateFormatter.LINK_FORMAT)
    };
};

export const getTwelveMonthsRequestDates = (date) => {
    let now = new moment(date, DateFormatter.LINK_FORMAT);

    return {
        endDate: now.endOf('months').format(DateFormatter.LINK_FORMAT),
        startDate: now
            .subtract(11, 'months')
            .startOf('months')
            .format(DateFormatter.LINK_FORMAT)
    };
};

export const get52WeeksRequestDates = (endDate) => {
    let end = new moment(endDate, DateFormatter.LINK_FORMAT);

    return {
        endDate: end.format(DateFormatter.LINK_FORMAT),
        startDate: end
            .subtract(52, 'weeks')
            .add(1, 'd')
            .format(DateFormatter.LINK_FORMAT)
    };
};

export const weightTime = (weight) => {
    return (
        weight.timestampGMT ||
        moment(weight.calendarDate, DateFormatter.LINK_FORMAT)
            .startOf('d')
            .valueOf()
    );
};

const getDateRangeForMultipleDaysRequests = (currentTab) => {
    switch (currentTab) {
        case TABS.SEVEN_DAYS:
            return MULTIPLE_DAYS_REQUEST_KEYS.SEVEN_DAYS;
        case TABS.FOUR_WEEKS:
            return MULTIPLE_DAYS_REQUEST_KEYS.FOUR_WEEKS;
        case TABS.ONE_MONTH:
            return MULTIPLE_DAYS_REQUEST_KEYS.ONE_MONTH;
    }
};

export const getActionToDispatch = (currentTab, currentPageDate) => {
    let returnObject = null;

    if (currentTab === TABS.ONE_DAY || currentTab === null) {
        returnObject = {
            type: FETCH_DAY_WEIGHTS,
            date: currentPageDate,
            cacheTime: true
        };
    } else if (currentTab === TABS.SEVEN_DAYS || currentTab === TABS.FOUR_WEEKS || currentTab === TABS.ONE_MONTH) {
        let dates =
            currentTab === TABS.FOUR_WEEKS
                ? getFourWeeksRequestDates(currentPageDate)
                : currentTab === TABS.SEVEN_DAYS
                ? getSevenDaysRequestDates(currentPageDate)
                : getOneMonthRequestDates(currentPageDate);

        returnObject = {
            type: FETCH_MULTIPLE_DAYS_WEIGHTS,
            dates,
            dateRange: getDateRangeForMultipleDaysRequests(currentTab),
            cacheTime: true
        };
    } else if (currentTab === TABS.FIFTY_TWO_WEEKS) {
        let dates = getTwelveMonthsRequestDates(currentPageDate);

        returnObject = {
            type: FETCH_MONTHLY_WEIGHTS,
            dates,
            cacheTime: true
        };
    }

    return returnObject;
};

export const getYAxisTitle = (selectedDataTypes, width, displayWeightSecondInput, weightDisplayUnit) => {
    if (width < 993) {
        return '';
    }

    let result = '';

    selectedDataTypes.forEach((selectedDataType) => {
        switch (selectedDataType) {
            case AVAILABLE_WEIGHT_METRICS.weight:
            case AVAILABLE_WEIGHT_METRICS.muscleMass:
            case AVAILABLE_WEIGHT_METRICS.boneMass:
                if (!displayWeightSecondInput) {
                    result +=
                        getDisplayTypeUnit(weightDisplayUnit, this) +
                        ' (' +
                        i18next.t('units:' + Personalizer.getWeightUnitKey(weightDisplayUnit)) +
                        ')';
                } else {
                    result += Localizer.localize('weight_unit_statute_uk_title');
                }
                break;
            case AVAILABLE_WEIGHT_METRICS.bodyFat:
            case AVAILABLE_WEIGHT_METRICS.bodyWater:
                result += i18next.t('units:percent');
                break;
            case AVAILABLE_WEIGHT_METRICS.bmi:
                result += i18next.t('weightwidget:bmi');
                break;
        }
        result += ' ';
    });
    return result;
};

const getDisplayTypeUnit = (weightDisplayUnit) => {
    return i18next.t('weightwidget:' + getWeightLongUnit(weightDisplayUnit));
};

const getWeightLongUnit = (weightDisplayUnit) => {
    if (Personalizer.getWeightUnitKey(weightDisplayUnit) === 'weight_unit_metric') {
        return 'kilograms';
    } else if (Personalizer.getWeightUnitKey(weightDisplayUnit) === 'weight_unit_statute') {
        return 'pounds';
    } else {
        return 'stones';
    }
};

export const formatValue = (weight, selectedDataType, displayWeightSecondInput, weightDisplayUnit) => {
    if (weight) {
        let value = getValue(weight[selectedDataType], selectedDataType, displayWeightSecondInput, weightDisplayUnit);
        if (!value) {
            return null;
        }
        const weightingTime = weightTime(weight);
        let result = {
            x: weightingTime,
            y: value || Personalizer.personalizeWeight(weight[selectedDataType], false, weightDisplayUnit),
            timestampGMT: weight.timestampGMT,
            weightObject: weight
        };
        if (result.y !== '--') {
            result.y = Number(result.y);
        }
        return result;
    }
};

export const getValue = (
    rawValue,
    selectedDataType,
    displayWeightSecondInput,
    weightDisplayUnit,
    optionalNumberFormat
) => {
    if (!rawValue) {
        return null;
    }
    switch (selectedDataType) {
        case AVAILABLE_WEIGHT_METRICS.weight:
        case AVAILABLE_WEIGHT_METRICS.muscleMass:
        case AVAILABLE_WEIGHT_METRICS.boneMass:
            if (!displayWeightSecondInput) {
                return optionalNumberFormat
                    ? Personalizer.personalizeWeight(rawValue, optionalNumberFormat, weightDisplayUnit)
                    : Personalizer.convertWeight(rawValue, weightDisplayUnit);
            } else {
                return Personalizer.convertWeightToGivenUnit(rawValue, Personalizer.WeightUnitKeys.STATUTE_UK);
            }

        case AVAILABLE_WEIGHT_METRICS.bodyFat:
        case AVAILABLE_WEIGHT_METRICS.bodyWater:
            return parseFloat(
                Personalizer.personalizeWeightWithoutConvert(rawValue, NumberFormats.PRECISION1).replace(/,/g, '.')
            );
        case AVAILABLE_WEIGHT_METRICS.bmi:
        case AVAILABLE_WEIGHT_METRICS.visceralFat:
            return parseFloat(
                Personalizer.personalizeWeightWithoutConvert(rawValue, NumberFormats.PRECISION2).replace(/,/g, '.')
            );
        case AVAILABLE_WEIGHT_METRICS.caloricIntake:
            return parseFloat(
                Personalizer.personalizeWeightWithoutConvert(rawValue, NumberFormats.NO_DECIMAL).replace(/,/g, '.')
            );
        case AVAILABLE_WEIGHT_METRICS.physiqueRating:
            return rawValue;
        case AVAILABLE_WEIGHT_METRICS.metabolicAge:
            return Math.floor(rawValue / 31536000000);
    }
};

export const getMeasurementUnitForDataType = (selectedDataType, weightDisplayUnit) => {
    switch (selectedDataType) {
        case AVAILABLE_WEIGHT_METRICS.weight:
        case AVAILABLE_WEIGHT_METRICS.muscleMass:
        case AVAILABLE_WEIGHT_METRICS.boneMass:
            return i18next.t(`units:${Personalizer.getWeightUnitKey(weightDisplayUnit)}`);
        case AVAILABLE_WEIGHT_METRICS.bodyFat:
        case AVAILABLE_WEIGHT_METRICS.bodyWater:
            return '%';
        case AVAILABLE_WEIGHT_METRICS.bmi:
            return i18next.t('weightwidget:bmi');
        case AVAILABLE_WEIGHT_METRICS.metabolicAge:
            return i18next.t('weightwidget:age_label');
        case AVAILABLE_WEIGHT_METRICS.caloricIntake:
            return i18next.t('units:calories_unit');
        default:
            return '';
    }
};

export const getMeasurementUnit = (value, selectedDataType, weightDisplayUnit) => {
    if (!value) {
        return null;
    }
    return getMeasurementUnitForDataType(selectedDataType, weightDisplayUnit);
};

export const userHasWeightScaleDevice = (devicesListObject) => {
    return (
        devicesListObject && devicesListObject.weightScaleDeviceCount && devicesListObject.weightScaleDeviceCount > 0
    );
};

export const getMainSeriesTitle = (selectedDataType) => {
    if (isWeightSelected(selectedDataType)) {
        return i18next.t('weightwidget:weigh_in');
    } else {
        return i18next.t(
            'weightwidget:' +
                selectedDataType
                    .split(/(?=[A-Z])/)
                    .join('_')
                    .toLowerCase()
        );
    }
};

export const getYAxisTickInterval = (yMin, yMax) => {
    return Math.round((yMax - yMin) / 4);
};

export const isWeightSelected = (selectedDataType) => {
    return selectedDataType === AVAILABLE_WEIGHT_METRICS.weight;
};

export const calculateBMI = (weight, userSettings) => {
    let height = userSettings && userSettings.userData && userSettings.userData.height;
    if (height) {
        const heightInMeters = height / 100;
        const weightInKg = weight * ConversionFactors.GramsToKg;
        return weightInKg / (heightInMeters * heightInMeters);
    }
    return null;
};

export const getWeightForChart = (weight, displayWeightSecondInput, weightDisplayUnit) => {
    if (displayWeightSecondInput) {
        return Personalizer.convertWeightToGivenUnit(weight, weightDisplayUnit);
    }
    return returnStandardNumber(Personalizer.personalizeWeight(weight, false, weightDisplayUnit));
};

export const showMinMaxInTooltip = (tooltipWeights, displayWeightSecondInput) => {
    if (displayWeightSecondInput) {
        return tooltipWeights.value !== tooltipWeights.minWeight || tooltipWeights.value !== tooltipWeights.maxWeight;
    }
    return (
        Number(tooltipWeights.value || tooltipWeights.averageWeight.weight) !==
            returnStandardNumber(tooltipWeights.minWeight) ||
        Number(tooltipWeights.value || tooltipWeights.averageWeight.weight) !==
            returnStandardNumber(tooltipWeights.maxWeight)
    );
};

const returnStandardNumber = (value) => {
    return Number(value.toString().replace(/,/g, '.'));
};

export const tooltipPositionerAtTopOfTheChart = (labelWidth, labelHeight, point, chart) => {
    let plotLeft = chart.plotLeft;
    let plotTop = chart.plotTop;
    let plotWidth = chart.plotWidth;
    let x = point.plotX + 15;
    let y = plotTop - labelHeight - 10;

    if (x + labelWidth > plotLeft + plotWidth) {
        //2 px to account for the border
        x -= x + labelWidth + 2 - (plotLeft + plotWidth);
    }

    return {
        x: x,
        y: y
    };
};

export const getMetricsData = (weightsSummaries) => {
    let metrics = {};
    for (let metric in AVAILABLE_WEIGHT_METRICS) {
        metrics[metric] = !!weightsSummaries.find((weight) => {
            if (weight.latestWeight) {
                //multiple days weights
                return weight.latestWeight[metric];
            } else if (weight.averageWeight) {
                //monthly weights
                return weight.averageWeight[metric];
            } else if (weight[metric]) {
                //one day weight check
                return weight[metric];
            }
        });
    }
    return metrics;
};

export const hasTanitaData = (weight) => {
    let data = weight.latestWeight || weight.averageWeight || weight || {};
    if (
        data &&
        weight.allWeightMetrics &&
        (data[AVAILABLE_WEIGHT_METRICS.caloricIntake] === null ||
            data[AVAILABLE_WEIGHT_METRICS.metabolicAge] === null ||
            data[AVAILABLE_WEIGHT_METRICS.visceralFat] === null ||
            data[AVAILABLE_WEIGHT_METRICS.physiqueRating] === null)
    ) {
        data = weight.allWeightMetrics;
        return (
            data.find((weight) => weight[AVAILABLE_WEIGHT_METRICS.caloricIntake]) ||
            data.find((weight) => weight[AVAILABLE_WEIGHT_METRICS.metabolicAge]) ||
            data.find((weight) => weight[AVAILABLE_WEIGHT_METRICS.visceralFat]) ||
            data.find((weight) => weight[AVAILABLE_WEIGHT_METRICS.physiqueRating])
        );
    }
    return (
        data[AVAILABLE_WEIGHT_METRICS.caloricIntake] ||
        data[AVAILABLE_WEIGHT_METRICS.metabolicAge] ||
        data[AVAILABLE_WEIGHT_METRICS.visceralFat] ||
        data[AVAILABLE_WEIGHT_METRICS.physiqueRating]
    );
};

export const formatWeight = (value, weightDisplayUnit, formatDelta) => {
    if (value !== 0 && !value) {
        return '--';
    }
    let personalizedWeight = Personalizer.personalizeWeight(value, NumberFormats.PRECISION1, weightDisplayUnit);

    if (formatDelta) {
        personalizedWeight = Personalizer.personalizeWeight(
            Math.abs(value),
            NumberFormats.PRECISION1,
            weightDisplayUnit
        );
    }

    return personalizedWeight + ' ' + i18next.t(`units:${Personalizer.getWeightUnitKey(weightDisplayUnit)}`);
};

export const getChartYMin = (weightList, selectedDataType, latestWeightGoal) => {
    let min = Number.MAX_SAFE_INTEGER;
    weightList.forEach((weight) => {
        let data = weight.latestWeight || weight.averageWeight || weight || {};
        let currentValue = data[selectedDataType];
        if (currentValue && currentValue < min) {
            min = currentValue;
        }
    });
    if (latestWeightGoal && isWeightSelected(selectedDataType)) {
        let goal = Number(latestWeightGoal.goalValue);
        if (goal < min) {
            min = goal;
        }
    }
    return min;
};

export const getChartYMax = (weightList, selectedDataType, latestWeightGoal) => {
    let max = 0;
    weightList.forEach((weight) => {
        let data = weight.latestWeight || weight.averageWeight || weight || {};
        let currentValue = data[selectedDataType];
        if (currentValue && currentValue > max) {
            max = currentValue;
        }
    });
    if (latestWeightGoal && isWeightSelected(selectedDataType)) {
        let goal = Number(latestWeightGoal.goalValue);
        if (goal > max) {
            max = goal;
        }
    }
    return max;
};

export const getWeightObjectFromWeightList = (weight) => {
    return weight.latestWeight || weight.averageWeight || weight || {};
};

export const getExportRow = (data, weightDisplayUnit, displayWeightSecondInput) => {
    let row = [];
    row.push(DateFormatter.formatTime(new Date(weightTime(data))));

    let weight, weightDelta, muscleMass, boneMass;
    if (displayWeightSecondInput) {
        weight = formatWeightInStLbs(Personalizer.convertGramsToStLbs(data.weight), weightDisplayUnit);
        weightDelta = formatWeightInStLbs(Personalizer.convertGramsToStLbs(data.weightDelta), weightDisplayUnit, true);
        muscleMass = formatWeightInStLbs(Personalizer.convertGramsToStLbs(data.muscleMass), weightDisplayUnit);
        boneMass = formatWeightInStLbs(Personalizer.convertGramsToStLbs(data.boneMass), weightDisplayUnit);
    } else {
        weight = replaceCommaForCsv(formatWeight(data.weight, weightDisplayUnit));
        weightDelta = replaceCommaForCsv(formatWeight(data.weightDelta, weightDisplayUnit, true));
        muscleMass = data.muscleMass ? replaceCommaForCsv(formatWeight(data.muscleMass, weightDisplayUnit)) : '--';
        boneMass = data.boneMass ? replaceCommaForCsv(formatWeight(data.boneMass, weightDisplayUnit)) : '--';
    }

    row.push(weight);
    row.push(weightDelta);
    row.push(data.bmi ? replaceCommaForCsv(Math.round(data.bmi * 10) / 10) : '--');
    row.push(data.bodyFat ? replaceCommaForCsv(Math.round(data.bodyFat * 10) / 10 + ' %') : '--');
    row.push(muscleMass);
    row.push(boneMass);
    row.push(
        data.bodyWater
            ? replaceCommaForCsv(
                  Personalizer.personalizeWeightWithoutConvert(data.bodyWater, NumberFormats.PRECISION1) + ' %'
              )
            : '--'
    );

    if (hasTanitaData(data)) {
        row.push(getExportTanitaEntry(data, AVAILABLE_WEIGHT_METRICS.physiqueRating));
        row.push(getExportTanitaEntry(data, AVAILABLE_WEIGHT_METRICS.metabolicAge));
        row.push(getExportTanitaEntry(data, AVAILABLE_WEIGHT_METRICS.visceralFat));
        row.push(getExportTanitaEntry(data, AVAILABLE_WEIGHT_METRICS.caloricIntake));
    }

    return row;
};

function replaceCommaForCsv(value) {
    let result = '' + value;
    return result.replace(/,/g, '.');
}

function getExportTanitaEntry(data, metric) {
    let tanita = getValue(data[metric], metric);
    if (!tanita) {
        return '--';
    }
    return replaceCommaForCsv(tanita + ' ' + getMeasurementUnitForDataType(metric));
}

export const getCsvData = (rows) => {
    let data = '\ufeff';
    rows.forEach((dataRow) => {
        dataRow.forEach((dataValue) => {
            data += dataValue + ',';
        });
        data += '\n';
    });
    return data;
};

export const getExportDataHeaders = (isAverages, hasTanita) => {
    const headers = [];
    headers.push(i18next.t('units:label_time'));
    if (isAverages) {
        headers.push(i18next.t('weightwidget:avg_weigth'));
        headers.push(i18next.t('email-summary-reports:email_change'));
        headers.push(i18next.t('weightwidget:avg_bmi'));
        headers.push(i18next.t('weightwidget:avg_body_fat'));
        headers.push(i18next.t('weightwidget:avg_muscle_mass'));
        headers.push(i18next.t('weightwidget:avg_bone_mass'));
        headers.push(i18next.t('weightwidget:avg_body_water'));
        if (hasTanita) {
            headers.push(i18next.t('weightwidget:avg_physique_rating'));
            headers.push(i18next.t('weightwidget:avg_visceral_fat'));
            headers.push(i18next.t('weightwidget:avg_metabolic_age'));
            headers.push(i18next.t('weightwidget:avg_caloric_intake'));
        }
    } else {
        headers.push(i18next.t('units:label_weight'));
        headers.push(i18next.t('email-summary-reports:email_change'));
        headers.push(i18next.t('weightwidget:bmi'));
        headers.push(i18next.t('weightwidget:body_fat'));
        headers.push(i18next.t('weightwidget:muscle_mass'));
        headers.push(i18next.t('weightwidget:bone_mass'));
        headers.push(i18next.t('weightwidget:body_water'));
        if (hasTanita) {
            headers.push(i18next.t('weightwidget:physique_rating'));
            headers.push(i18next.t('weightwidget:visceral_fat'));
            headers.push(i18next.t('weightwidget:metabolic_age'));
            headers.push(i18next.t('weightwidget:caloric_intake'));
        }
    }
    return headers;
};

export const hasTanitaDataInList = (weightList) => {
    return !!weightList.find((weight) => {
        return hasTanitaData(weight);
    });
};

export const getWeightExportData = (weightList, weightDisplayUnit, displayWeightSecondInput, isAverages) => {
    const hasTanitaData = hasTanitaDataInList(weightList);

    const rows = [getExportDataHeaders(isAverages, hasTanitaData)];
    weightList.forEach((entry) => {
        if (entry.allWeightMetrics) {
            const date = DateFormatter.formatMonthDayYear(
                new moment(entry.summaryDate, DateFormatter.LINK_FORMAT).toDate()
            );
            const parsedDate = `\".${date}\"`.replace('.', ' ');
            rows.push([parsedDate]);
            entry.allWeightMetrics.forEach((singleEntry) => {
                rows.push(getExportRow(singleEntry, weightDisplayUnit, displayWeightSecondInput));
            });
        } else {
            let data = entry.latestWeight || entry.averageWeight || entry || {};
            rows.push(getExportRow(data, weightDisplayUnit, displayWeightSecondInput));
        }
    });

    return getCsvData(rows);
};

export const formatWeightInStLbs = (value, weightDisplayUnit, formatDelta) => {
    if ((value !== 0 && !value) || (value.stone === 0 && value.pounds === 0 && !formatDelta)) {
        return '--';
    }
    if (formatDelta) {
        value.stone = Math.abs(value.stone);
        value.pounds = Math.abs(value.pounds);
    }
    return (
        value.stone +
        ' ' +
        Localizer.localize(weightDisplayUnit) +
        ' ' +
        value.pounds +
        ' ' +
        i18next.t(`units:${Personalizer.getWeightUnitKey()}`)
    );
};

export const shouldConvertToStLbs = (selectedDataType) => {
    return (
        AVAILABLE_WEIGHT_METRICS.weight === selectedDataType ||
        AVAILABLE_WEIGHT_METRICS.muscleMass === selectedDataType ||
        AVAILABLE_WEIGHT_METRICS.boneMass === selectedDataType
    );
};

export const getGoalValue = (latestWeightGoal, displayWeightSecondInput, weightDisplayUnit) => {
    if (!latestWeightGoal) {
        return null;
    }
    if (displayWeightSecondInput) {
        return formatWeightInStLbs(Personalizer.convertGramsToStLbs(latestWeightGoal), weightDisplayUnit);
    }
    return Personalizer.personalizeWeight(latestWeightGoal, NumberFormats.PRECISION1, weightDisplayUnit);
};

export const getGoalLegendLabel = (latestWeightGoal, displayWeightSecondInput, weightDisplayUnit) => {
    const goal = getGoalValue(latestWeightGoal, displayWeightSecondInput, weightDisplayUnit);
    const measurementUnit = displayWeightSecondInput ? '' : Personalizer.getWeightUnitKey(weightDisplayUnit);
    if (!goal) {
        return null;
    }
    return displayWeightSecondInput
        ? `${i18next.t('weightwidget:goal')} (${goal})`
        : `${i18next.t('weightwidget:goal')} (${goal} ${i18next.t(`units:${measurementUnit}`)})`;
};

export const isDataTypeSelected = (dataTypes, dataType) => {
    return dataTypes.indexOf(dataType) > -1;
};

export const getMinMaxValue = (
    dataList,
    dataTypes,
    getMin,
    displaySecondInput,
    weightDisplayUnit,
    latestWeightGoal,
    weightGainGoal
) => {
    let result;
    dataList.forEach((entry) => {
        return dataTypes.forEach((dataType) => {
            let rawValue = getWeightObjectFromWeightList(entry)[dataType];
            const value = getValue(rawValue, dataType, displaySecondInput, weightDisplayUnit);
            if (!result) {
                result = value;
            }
            if (getMin) {
                if (value && value < result) {
                    result = value;
                }
            } else {
                if (value && value > result) {
                    result = value;
                }
            }
        });
    });
    let value, goal, gainGoal;
    if (latestWeightGoal && isDataTypeSelected(dataTypes, AVAILABLE_WEIGHT_METRICS.weight)) {
        goal = getValue(
            latestWeightGoal.goalValue,
            AVAILABLE_WEIGHT_METRICS.weight,
            displaySecondInput,
            weightDisplayUnit
        );
    }
    if (weightGainGoal && isDataTypeSelected(dataTypes, AVAILABLE_WEIGHT_METRICS.weight)) {
        gainGoal = getValue(weightGainGoal, AVAILABLE_WEIGHT_METRICS.weight, displaySecondInput, weightDisplayUnit);
    }

    if (!gainGoal || (goal < gainGoal && getMin) || (!getMin && goal > gainGoal)) {
        value = goal;
    } else if (!goal || (getMin && goal > gainGoal) || (!getMin && goal < gainGoal)) {
        value = gainGoal;
    }

    if (value) {
        if (!result) {
            result = value;
        }
        if (getMin) {
            if (value < result) {
                result = value;
            }
        } else {
            if (value > result) {
                result = value;
            }
        }
    }
    return result;
};
